import React, { useState, useEffect, useRef, forwardRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import { closeIcon } from '../../../../assets/assets'

const isEmpty = (ref) => {
  if (!ref) return true
  return !!ref.isEmpty()
}

const generateSigData = (ref) => {
  const data = ref?.toDataURL?.()
  if (!data || typeof data !== 'string' || !data.includes(',')) return ''
  return data.split(',')[1]
}

const FixSignatureCanvas = SignatureCanvas.default

const persistScribbleSubmission = (props, text) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = text
      ? [
          {
            label,
            value: text,
            question: id,
          },
        ]
      : []
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: text ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('ScribbleType autosave failed', { error: e })
  }
}

const completeScribbleTypeQuestion = (props, text) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = text
    ? [
        {
          label,
          value: text,
          question: id,
        },
      ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: text ? 'ANSWERED' : 'SKIPPED',
    answer,
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

const ScribbleType = forwardRef((props, ref) => {
  const {
    className,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const sigRef = useRef(null)
  const signingAreaRef = useRef(null)
  const [sigHeight, setSigHeight] = useState(256)
  const [sigWidth, setSigWidth] = useState(768)
  const [signed, setSigned] = useState(false)
  const [sigValue, setSigValue] = useState('')

  const signaturePadRatio = 0.5

  useEffect(() => {
    const raw = storedAnswer?.[0]?.value
    if (!raw) return
    raw.includes('data:image/png;base64,')
      ? sigRef?.current?.fromDataURL(raw)
      : sigRef?.current?.fromDataURL(`data:image/png;base64,${raw}`)
    setSigned(true)
    const persisted =
      raw.includes('data:image/png;base64,') && raw.includes(',')
        ? raw.split(',')[1]
        : raw
    setSigValue(persisted)
    if (!props.suppressAutoAdvance) {
      completeScribbleTypeQuestion(props, persisted)
    } else {
      persistScribbleSubmission(props, persisted)
    }
  }, [])

  const onResize = () => {
    setSigned(false)
    sigRef?.current?.clear()
    setSigValue('')
    setSigWidth(signingAreaRef?.current?.clientWidth)
    setSigHeight(signaturePadRatio * signingAreaRef?.current?.clientWidth)
  }

  useEffect(() => {
    setSigWidth(signingAreaRef?.current?.clientWidth)
    setSigHeight(signaturePadRatio * signingAreaRef?.current?.clientWidth)
    window.addEventListener('resize', onResize)
  }, [window.innerWidth])

  const { attributes, label, imageSrc } = question
  const { required } = attributes
  const _className = className ? ` ${className}` : ''

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div
          ref={ref}
          className={`survey-question scribble-entry${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label),
              }}
            />
            <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
          </p>
          <div className='signing-area' ref={signingAreaRef}>
            <FixSignatureCanvas
              penColor='black'
              canvasProps={{
                width: sigWidth || 512,
                height: sigHeight || 256,
                className: 'sigCanvas',
              }}
              ref={sigRef}
              onEnd={() => {
                setAllRequiredAnswered(false, id)
                const empty = isEmpty(sigRef?.current)
                const data = empty ? '' : generateSigData(sigRef?.current)
                setSigned(!empty)
                setSigValue(data)
                queueMicrotask(() => persistScribbleSubmission(props, data))
              }}
            />
            <div
              className='signing-area-clear-btn'
              onClick={() => {
                setAllRequiredAnswered(false, id)
                setSigned(false)
                sigRef?.current?.clear()
                setSigValue('')
                queueMicrotask(() => persistScribbleSubmission(props, ''))
              }}>
              <div className='clear-text'>Clear</div>
              <div className='clear-icon'>{closeIcon()}</div>
            </div>
          </div>
          {!isSibling && (
            <div className='character-count-row'>
              <QuestionFooter
                completed={!!sigValue}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() => completeScribbleTypeQuestion(props, sigValue)}
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            </div>
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default ScribbleType

ScribbleType.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  imageSrc: PropTypes.string,
}
